import React, { useMemo, useState } from 'react';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { useTranslation } from "react-i18next";
import TextField from '@shared/ui/atoms/TextField';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import styled from "styled-components";
import { Switch } from '@shared/ui/atoms/Switch';
import { useDispatch, useSelector } from "react-redux";
import { warningNotification } from "@shared/store/notification.duck";
import { isNumber } from "@shared/utils/TypeUtils";
import { selectAllOfficeEntities } from "@features/desks/store/advanceHotDeskingSetup.duck";
import { findOfficeInOffices, getRootOffice } from "@shared/utils/OfficeHelper";
import { selectAllDesksInEditingOffice } from "@features/desks/store/editOfficeLayout.duck";
import { Row } from '@shared/ui/atoms/StructuralLayout';
import { SpaceBetweenRow } from "../StyleComponents";
import { Checkbox } from '@shared/ui/atoms/Checkbox';
import { HorizontalSpacer } from '@shared/ui/atoms/VerticalSpacer';
import { GenericDropDownList } from '@shared/ui/molecules/GenericDropDownList';
import { TIMEZONES } from '@shared/utils/Timezones';
import moment from "moment";

function getDaysOfWeek() {
  // ISO day-of-week ids (1 = Monday .. 7 = Sunday) to match the backend.
  return [1, 2, 3, 4, 5, 6, 7].map(day => ({id: day, name: moment().day(day).format('dddd')}));
}

const OPEN_TIMES = Array.from({length: 24}, (_, hour) => {
  const label = `${hour.toString().padStart(2, '0')}:00`;
  return {id: label, name: label};
});

export function DeskBookingWindowSetting(props: Props) {
  const {office} = props;
  const dispatch = useDispatch();
  const desks = useSelector(selectAllDesksInEditingOffice);
  const allOffices = useSelector(selectAllOfficeEntities);
  const [enabled, setEnabled] = useState(office.officeBookingWindowWeeks > 0);
  const [days, setDays] = useState(office?.officeBookingWindowWeeks ?? 0);
  const [restrictAccessToTeamAdmins, setRestrictAccessToTeamAdmins] = useState(office?.restrictBookingWindowForTeamAdmins ?? false);
  const [openDay, setOpenDay] = useState(office?.bookingWindowOpenDay ?? 1);
  const [openTime, setOpenTime] = useState(office?.bookingWindowOpenTime ?? '00:00');
  const [timezone, setTimezone] = useState(office?.bookingWindowTimezone ?? 'UTC');
  const {t} = useTranslation();

  const shouldBeVisible = useMemo(() => {
    if (!office) return false;
    for (let desk of desks) {
      const desksOffice = findOfficeInOffices(allOffices, desk.officeId);
      if (desksOffice) {
        const rootOffice = getRootOffice(allOffices, desksOffice);
        if (rootOffice && rootOffice.id === office.id) {
          return true;
        }
      }
    }
    return false;
  }, [desks, office, allOffices])

  const updateText = (text: string) => {
    if (isNumber(text)) {
      const d = parseInt(text);
      setDays(d);
      props.onOfficeBookingWindowChanged(days)
    } else {
      dispatch(warningNotification('Must be a number'))
    }
  }

  const onEnabledChanged = (changed: boolean) => {
    if (!changed) {
      setDays(0);
      props.onOfficeBookingWindowChanged(0);
    }

    setEnabled(changed);
  }

  const update = () => {
    props.onOfficeBookingWindowChanged(days);
  }

  const updateRestrictBookingWindowForTeamAdmins = (val: boolean) => {
    setRestrictAccessToTeamAdmins(val);
    props.onRestrictBookingWindowForTeamAdminsChange(val);

  }

  const onOpenDayChange = (id: any) => {
    const day = parseInt(id);
    setOpenDay(day);
    props.onBookingWindowOpenDayChanged(day);
  }

  const onOpenTimeChange = (id: any) => {
    setOpenTime(id);
    props.onBookingWindowOpenTimeChanged(id);
  }

  const onTimezoneChange = (id: any) => {
    setTimezone(id);
    props.onBookingWindowTimezoneChanged(id);
  }

  if (!shouldBeVisible) {
    return null;
  }

  return (
    <>
      <Wrapper>
        <BodyRegular weight={600}>{t('settings.manage-office.desk-booking-window-title')}</BodyRegular>
        <Switch value={enabled} onChange={onEnabledChanged} disabled={props.disabled} />
      </Wrapper>
      <BodyRegular style={{marginBottom: 16}}>{t('settings.manage-office.desk-booking-window-description')}</BodyRegular>
      {enabled && <>
        <SpaceBetweenRow>
          <Checkbox value={true} disabled={true} onChange={() => {}}
                    text={'settings.editing-future-whereabouts.applies-to-standard-users'} />
        </SpaceBetweenRow>

        <SpaceBetweenRow style={{marginBottom: 20}}>
          <Checkbox value={restrictAccessToTeamAdmins}
                    onChange={(val: boolean) => updateRestrictBookingWindowForTeamAdmins(val)}
                    text={'settings.editing-future-whereabouts.applies-to-team-admins'} />
        </SpaceBetweenRow>

        <Row>
          <BodyRegular weight={600} style={{marginTop: 6, marginRight: 24}}>{t('settings.manage-office.desk-booking-window-weeks')}</BodyRegular>
          <TextField onChange={updateText}
                     onBlur={update}
                     value={days.toString()}
                     style={{width:60, marginBottom: 0}}
                     disabled={props.disabled} />
        </Row>

        <BodyRegular style={{marginTop: 20, marginBottom: 12}}>{t('settings.manage-office.desk-booking-window-opens-description')}</BodyRegular>

        <SpaceBetweenRow>
          <BodyRegular weight={600} style={{whiteSpace: 'nowrap', marginRight: 16}}>{t('settings.manage-office.desk-booking-window-open-day')}</BodyRegular>
          <GenericDropDownList className={'dropdown'}
                               disabled={props.disabled}
                               onSelect={onOpenDayChange}
                               items={getDaysOfWeek()}
                               placeholder={'settings.manage-office.desk-booking-window-open-day'}
                               selectedId={openDay} />
        </SpaceBetweenRow>

        <SpaceBetweenRow>
          <BodyRegular weight={600} style={{whiteSpace: 'nowrap', marginRight: 16}}>{t('settings.manage-office.desk-booking-window-open-time')}</BodyRegular>
          <GenericDropDownList className={'dropdown'}
                               disabled={props.disabled}
                               onSelect={onOpenTimeChange}
                               items={OPEN_TIMES}
                               placeholder={'settings.manage-office.desk-booking-window-open-time'}
                               selectedId={openTime} />
        </SpaceBetweenRow>

        <SpaceBetweenRow>
          <BodyRegular weight={600} style={{whiteSpace: 'nowrap', marginRight: 16}}>{t('settings.manage-office.desk-booking-window-timezone')}</BodyRegular>
          <GenericDropDownList className={'dropdown'}
                               disabled={props.disabled}
                               onSelect={onTimezoneChange}
                               items={TIMEZONES}
                               placeholder={'settings.manage-office.desk-booking-window-timezone'}
                               selectedId={timezone} />
        </SpaceBetweenRow>
      </>}
      <HorizontalSpacer />
    </>
  )
}

interface Props {
  office: OfficeEntity;
  onOfficeBookingWindowChanged: (days: number) => void;
  onRestrictBookingWindowForTeamAdminsChange: (val: boolean) => void;
  onBookingWindowOpenDayChanged: (day: number) => void;
  onBookingWindowOpenTimeChanged: (time: string) => void;
  onBookingWindowTimezoneChanged: (timezone: string) => void;
  disabled: boolean;
}

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 44px;
  
  .textField {
    max-width: 120px;
    margin-left: auto;
    margin-bottom: 0;
  }
  .switch {
    margin-left: 16px;
  }
`
